import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { getUsersFromCollaborationGraph } from '@atlassian/cross-join-nudges/services/collaborators';
import { getUsersWithProductAccess } from '@atlassian/cross-join-nudges/services/collaborators-perm-check';

import { fg } from '@confluence/feature-gating';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { showNudgeFlag } from '../ui/crossJoinNudgesFlags.tsx';

import {
	fireCacheDisabledAnalytics,
	fireExperimentError,
	fireMsrNotRecommendedAnalytics,
	fireSiteOptedOutAnalytics,
	getExperimentAnalytics,
} from './analytics.ts';
import { resolveCrossJoinNudges, CONF_TO_JIRA_INTEGRATION } from './resolve-cross-join-nudges.ts';
import { increaseFeatureVisitedCount, isFeatureVisited } from './feature-visited.ts';
import { setHasSeenNudgeInLast7Days } from './caching.ts';
import {
	resolveJpdCrossJoinNudges,
	CONF_TO_JPD_INTEGRATION,
} from './resolve-jpd-cross-join-nudges.ts';

export interface CrossJoinNudgesInput {
	cloudId: string;
	userId: string | null;
	flags: FlagsStateContainer;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	push: (url: string, forceReload?: boolean | undefined) => void;
}

// NOTE: This file is for old nudges - setup new files specifically per nudge experiment and add all helper functions there to avoid conflict
export const triggerJiraCrossJoinNudge = async ({
	cloudId,
	userId,
	flags,
	createAnalyticsEvent,
	push,
	getMSRNudgeRecommendations,
}: CrossJoinNudgesInput & {
	getMSRNudgeRecommendations: () => Promise<string[]>;
}): Promise<void> => {
	const analytics = getExperimentAnalytics(CONF_TO_JIRA_INTEGRATION, createAnalyticsEvent);

	try {
		if (!userId || (await isFeatureVisited(CONF_TO_JIRA_INTEGRATION))) {
			return;
		}

		const response = await resolveCrossJoinNudges({ userId, cloudId, getMSRNudgeRecommendations });

		if (response.ineligibilityReason === null) {
			await showNudgeFlag({
				product: 'jira-software',
				integration: CONF_TO_JIRA_INTEGRATION,
				userId,
				cloudId,
				collaborators: response.collaborators,
				createAnalyticsEvent,
				flags,
				pushRoute: push,
				onDisplayed: fg('con-cjn-fix-orchestrator-block-handling-gate')
					? () => {
							increaseFeatureVisitedCount(CONF_TO_JIRA_INTEGRATION);
							void setHasSeenNudgeInLast7Days();
						}
					: undefined,
			});

			if (!fg('con-cjn-fix-orchestrator-block-handling-gate')) {
				increaseFeatureVisitedCount(CONF_TO_JIRA_INTEGRATION);
				// this is used in the eligibility check for loom nudge
				await setHasSeenNudgeInLast7Days();
			}
		} else if (response.ineligibilityReason === 'SITE_OPTED_OUT') {
			fireSiteOptedOutAnalytics(analytics);
		} else if (response.ineligibilityReason === 'MSR_NUDGE_NOT_RECOMMENDED') {
			fireMsrNotRecommendedAnalytics(analytics, 'cjn-conf-jira-v3');
		} else if (response.ineligibilityReason === 'CACHE_DISABLED') {
			fireCacheDisabledAnalytics(analytics);
		}
	} catch (error: any) {
		fireExperimentError(analytics, error);
	}
};

type CrossJoinNudgesEligibilityInput = Pick<
	CrossJoinNudgesInput,
	'cloudId' | 'userId' | 'createAnalyticsEvent'
> & {
	getMSRNudgeRecommendations: () => Promise<string[]>;
};

export const checkJpdCrossJoinNudgeEligibility = async ({
	cloudId,
	userId,
	createAnalyticsEvent,
	getMSRNudgeRecommendations,
}: CrossJoinNudgesEligibilityInput): Promise<boolean> => {
	const analytics = getExperimentAnalytics(CONF_TO_JPD_INTEGRATION, createAnalyticsEvent);

	try {
		if (!userId) {
			return false;
		}

		if (await isFeatureVisited(CONF_TO_JPD_INTEGRATION)) {
			return false;
		}

		const eligibilityResponse = await resolveJpdCrossJoinNudges(cloudId, userId);

		if (!eligibilityResponse.eligible) {
			if (eligibilityResponse.ineligibilityReason === 'SITE_OPTED_OUT') {
				fireSiteOptedOutAnalytics(analytics);
			} else if (eligibilityResponse.ineligibilityReason === 'CACHE_DISABLED') {
				fireCacheDisabledAnalytics(analytics);
			}
			return false;
		}

		const recommendations = await getMSRNudgeRecommendations();

		if (!recommendations.includes('cjn-conf-jpd')) {
			fireMsrNotRecommendedAnalytics(analytics, 'cjn-conf-jpd');
			return false;
		}

		return true;
	} catch (error: any) {
		fireExperimentError(analytics, error);
		return false;
	}
};

export const showJpdCrossJoinNudge = async ({
	cloudId,
	userId,
	flags,
	createAnalyticsEvent,
	push,
}: CrossJoinNudgesInput): Promise<void> => {
	const analytics = getExperimentAnalytics(CONF_TO_JPD_INTEGRATION, createAnalyticsEvent);

	try {
		if (!userId) {
			return;
		}

		const collaborators = await getUsersFromCollaborationGraph(
			CONF_TO_JPD_INTEGRATION,
			cloudId,
			userId,
		);
		const collaboratorsOnJpd = await getUsersWithProductAccess(
			'jira-product-discovery',
			cloudId,
			collaborators,
		);

		await showNudgeFlag({
			product: 'jira-product-discovery',
			integration: CONF_TO_JPD_INTEGRATION,
			userId,
			cloudId,
			collaborators: collaboratorsOnJpd,
			createAnalyticsEvent,
			flags,
			pushRoute: push,
			onDisplayed: fg('con-cjn-fix-orchestrator-block-handling-gate')
				? () => {
						increaseFeatureVisitedCount(CONF_TO_JPD_INTEGRATION);
						void setHasSeenNudgeInLast7Days();
					}
				: undefined,
		});

		if (!fg('con-cjn-fix-orchestrator-block-handling-gate')) {
			increaseFeatureVisitedCount(CONF_TO_JPD_INTEGRATION);
			// this is used in the eligibility check for loom nudge
			await setHasSeenNudgeInLast7Days();
		}
	} catch (error: any) {
		fireExperimentError(analytics, error);
	}
};
